import { useEffect, useRef, useState } from "react";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/56011b06-510c-40c9-8082-6148cb9e8b7f/images/1bab5369b3be02dd21208e992d458afee0225d1568f45354629fa24465578ed3.png";

export default function Hero() {
  const ref = useRef(null);
  const timeoutRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          io.disconnect();
          // Brief beat on the initial (blank) state, then reveal.
          timeoutRef.current = setTimeout(() => {
            setRevealed(true);
            window.dispatchEvent(new Event("hero:revealed"));
          }, 650);
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const connector = `transition-opacity duration-700 ease-out ${
    revealed ? "opacity-100" : "opacity-0"
  }`;

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden grain bg-[#F7F5F0]"
    >
      {/* Photographic background — hidden in the initial state, fades/slides in on reveal */}
      <div
        data-testid="hero-background"
        className={`absolute inset-0 z-0 transition-all duration-[1200ms] ease-out ${
          revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <img
          src={HERO_IMG}
          alt="Ground Work atmospheric backdrop"
          className="w-full h-full object-cover animate-ken-burns"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(28,28,25,0.78) 0%, rgba(28,28,25,0.60) 30%, rgba(28,28,25,0.45) 58%, rgba(28,28,25,0.30) 78%, rgba(247,245,240,0.85) 94%, #F7F5F0 100%)",
          }}
        />
      </div>

      {/* Headline */}
      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 md:px-12 pt-44 md:pt-60 pb-32">
        <h1
          data-testid="hero-headline"
          className={`font-display text-6xl sm:text-7xl lg:text-[9rem] font-light tracking-tight leading-[1.04] max-w-6xl transition-colors duration-700 ${
            revealed ? "text-[#F7F5F0]" : "text-[#1C1C19]"
          }`}
        >
          <span className={connector}>Building</span>{" "}
          <span
            className={`inline-block italic text-[#F2C14E] transition-transform duration-[900ms] ease-out ${
              revealed ? "translate-x-0 translate-y-0" : "-translate-y-8 -translate-x-2"
            }`}
          >
            brands
          </span>
          <br />
          <span className={connector}>ready to</span>{" "}
          <span
            className={`inline-block italic text-[#E26D5C] transition-transform duration-[900ms] ease-out ${
              revealed ? "translate-x-0 translate-y-0" : "translate-y-8 translate-x-2"
            }`}
          >
            bloom.
          </span>
        </h1>

        <div
          className={`mt-16 md:mt-24 grid md:grid-cols-12 gap-10 items-end transition-all duration-1000 ease-out ${
            revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <p
            data-testid="hero-subtext"
            className="md:col-span-5 text-[#F7F5F0] text-lg lg:text-xl font-light leading-relaxed [text-shadow:_0_1px_18px_rgba(0,0,0,0.45)]"
          >
            GroundWork is a brand, marketing and growth partner for businesses at an inflection point.
            Bring us the problem: a brand that isn't landing, growth that's stalled, a story no one's telling right.
            We'll tell you what's actually broken before we tell you what to build.
            </p>

      
          <div className="md:col-span-4 md:col-start-9 flex flex-col sm:flex-row items-start gap-3">
            <a
              href="#contact"
              data-testid="hero-cta-work"
              className="btn-magnetic inline-flex items-center gap-3 px-5 py-3.5 md:py-4 rounded-full bg-[#F7F5F0] text-[#1C1C19] text-xs md:text-sm font-semibold uppercase tracking-widest"
            >
              Build with us
              <ArrowDownRight size={16} />
            </a>
            <a
              href="#how-we-work"
              data-testid="hero-cta-pitch"
              className="btn-magnetic inline-flex items-center gap-3 px-5 py-3.5 md:py-4 rounded-full border border-[#F7F5F0]/60 text-[#F7F5F0] text-xs md:text-sm font-semibold uppercase tracking-widest hover:bg-[#F7F5F0] hover:text-[#1C1C19]"
            >
              See how we work
              <ArrowDownRight size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute z-10 bottom-8 left-0 right-0 flex justify-center">
        <div className="flex flex-col items-center gap-2 text-[#1C1C19]/70 text-[10px] uppercase tracking-[0.3em]">
          <span>Scroll</span>
          <span className="w-px h-10 bg-[#1C1C19]/50" />
        </div>
      </div>
    </section>
  );
}
